import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";
import i18n from "@/i18n";
import { normalizeAllowedBehaviors } from "@/lib/appointments/availability";

export interface CatalogPackageTemplateItem {
  id: string;
  item_id: string | null;
  item_name_snapshot: string;
  unit_price_snapshot: number;
  sessions_total: number;
  is_free_line: boolean;
  sort_order: number;
}

export interface CatalogPackageTemplate {
  id: string;
  item_id: string;
  item_name: string;
  sale_price: number;
  controls_sessions: boolean;
  package_subtype_id: string | null;
  package_subtype_name: string | null;
  allowed_appointment_types: string[];
  charge_mode: "antecipado" | "por_sessao" | "parcelado" | "no_final";
  default_sessions: number;
  notes: string | null;
  items: CatalogPackageTemplateItem[];
}

type RawPackageItem = {
  id: string;
  name: string;
  sale_price: number | null;
  package_subtype_id?: string | null;
  package_subtype?: { name?: string | null } | null;
  allowed_appointment_types?: Array<{ appointment_type?: string | null }> | null;
};

interface UpsertCatalogPackageTemplateInput {
  itemId: string;
  chargeMode: CatalogPackageTemplate["charge_mode"];
  controlsSessions?: boolean;
  defaultSessions: number;
  notes?: string | null;
  controls_sessions?: boolean;
  items: Array<{
    itemId?: string | null;
    itemNameSnapshot: string;
    unitPriceSnapshot: number;
    sessionsTotal: number;
    isFreeLine: boolean;
    sortOrder: number;
  }>;
}

const normalizeTemplate = (row: {
  id: string;
  item_id: string;
  charge_mode: CatalogPackageTemplate["charge_mode"];
  default_sessions: number;
  notes?: string | null;
  controls_sessions?: boolean | null;
  item?: {
    name?: string | null;
    sale_price?: number | null;
    package_subtype_id?: string | null;
    package_subtype?: { name?: string | null } | null;
    allowed_appointment_types?: Array<{ appointment_type?: string | null }> | null;
  } | null;
  items?: Array<{
    id: string;
    item_id?: string | null;
    item_name_snapshot?: string;
    unit_price_snapshot?: number | null;
    sessions_total?: number;
    is_free_line?: boolean | null;
    sort_order?: number;
  }>;
}): CatalogPackageTemplate => ({
  id: row.id,
  item_id: row.item_id,
  item_name: row.item?.name ?? i18n.t("erp:common.package"),
  sale_price: row.item?.sale_price ?? 0,
  controls_sessions: row.controls_sessions ?? true,
  package_subtype_id: row.item?.package_subtype_id ?? null,
  package_subtype_name: row.item?.package_subtype?.name ?? null,
  allowed_appointment_types: normalizeAllowedBehaviors(
    row.item?.allowed_appointment_types?.map((entry) => entry.appointment_type ?? null) ?? [],
  ),
  charge_mode: row.charge_mode,
  default_sessions: row.default_sessions,
  notes: row.notes ?? null,
  items: [...(row.items ?? [])]
    .map((item) => ({
      id: item.id,
      item_id: item.item_id ?? null,
      item_name_snapshot: item.item_name_snapshot ?? "",
      unit_price_snapshot: item.unit_price_snapshot ?? 0,
      sessions_total: item.sessions_total ?? 1,
      is_free_line: item.is_free_line ?? false,
      sort_order: item.sort_order ?? 0,
    }))
    .sort((a, b) => a.sort_order - b.sort_order),
});

export const useCatalogPackageTemplates = () => {
  const { data: customerId } = useCurrentClinicCustomerId();

  return useQuery({
    queryKey: ["catalog-package-templates", customerId],
    enabled: Boolean(customerId),
    queryFn: async (): Promise<CatalogPackageTemplate[]> => {
      if (!customerId) return [];

      const { data: packageItems, error: packageItemsError } = await supabase
        .from("items")
        .select("id, name, sale_price, allowed_appointment_types:item_allowed_appointment_types(appointment_type)")
        .eq("customer_id", customerId)
        .eq("type", "PACOTE")
        .is("deleted_at", null)
        .order("name");

      if (packageItemsError) throw packageItemsError;
      const packageItemIds = (packageItems ?? []).map((row) => row.id);
      if (packageItemIds.length === 0) return [];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("catalog_packages")
        .select(`
          id,
          item_id,
          charge_mode,
          controls_sessions,
          default_sessions,
          notes,
          item:items(name, sale_price, package_subtype_id, package_subtype:package_subtypes(name), allowed_appointment_types:item_allowed_appointment_types(appointment_type)),
          items:catalog_package_items(
            id,
            item_id,
            item_name_snapshot,
            unit_price_snapshot,
            sessions_total,
            is_free_line,
            sort_order
          )
        `)
        .eq("customer_id", customerId)
        .is("deleted_at", null)
        .in("item_id", packageItemIds)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const rows = ((data ?? []) as Array<{
        id: string;
        item_id: string;
        charge_mode: CatalogPackageTemplate["charge_mode"];
        controls_sessions?: boolean;
        default_sessions: number;
        notes?: string | null;
        item?: RawPackageItem | null;
        items?: Array<{
          id: string;
          item_id?: string | null;
          item_name_snapshot?: string;
          unit_price_snapshot?: number | null;
          sessions_total?: number;
          is_free_line?: boolean | null;
          sort_order?: number;
        }>;
      }>).map(normalizeTemplate);

      const templatesByItemId = new Map(rows.map((row) => [row.item_id, row]));

      const fallbackRows = (packageItems ?? [])
        .filter((catalogItem) => !templatesByItemId.has(catalogItem.id))
        .map((catalogItem) => ({
          id: `template-${catalogItem.id}`,
          item_id: catalogItem.id,
          item_name: catalogItem.name,
          sale_price: catalogItem.sale_price ?? 0,
          allowed_appointment_types: normalizeAllowedBehaviors(
            catalogItem.allowed_appointment_types?.map((entry) => entry.appointment_type ?? null) ?? [],
          ),
          controls_sessions: true,
          package_subtype_id: null,
          package_subtype_name: null,
          charge_mode: "antecipado" as const,
          default_sessions: 1,
          notes: null,
          items: [
            {
              id: `template-item-${catalogItem.id}`,
              item_id: catalogItem.id,
              item_name_snapshot: catalogItem.name,
              unit_price_snapshot: catalogItem.sale_price ?? 0,
              sessions_total: 1,
              is_free_line: false,
              sort_order: 0,
            },
          ],
        }));

      return [...rows, ...fallbackRows].sort((a, b) => a.item_name.localeCompare(b.item_name));
    },
  });
};

export const useCatalogPackageTemplate = (itemId?: string | null) => {
  const { data: customerId } = useCurrentClinicCustomerId();

  return useQuery({
    queryKey: ["catalog-package-template", customerId, itemId],
    enabled: Boolean(customerId && itemId),
    queryFn: async (): Promise<CatalogPackageTemplate | null> => {
      if (!customerId || !itemId) return null;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("catalog_packages")
        .select(`
          id,
          item_id,
          charge_mode,
          controls_sessions,
          default_sessions,
          notes,
          item:items(name, sale_price, package_subtype_id, package_subtype:package_subtypes(name), allowed_appointment_types:item_allowed_appointment_types(appointment_type)),
          items:catalog_package_items(
            id,
            item_id,
            item_name_snapshot,
            unit_price_snapshot,
            sessions_total,
            is_free_line,
            sort_order
          )
        `)
        .eq("customer_id", customerId)
        .eq("item_id", itemId)
        .is("deleted_at", null)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;
      return normalizeTemplate(data as {
        id: string;
        item_id: string;
        charge_mode: CatalogPackageTemplate["charge_mode"];
        controls_sessions?: boolean;
        default_sessions: number;
        notes?: string | null;
        item?: RawPackageItem | null;
        items?: Array<{
          id: string;
          item_id?: string | null;
          item_name_snapshot?: string;
          unit_price_snapshot?: number | null;
          sessions_total?: number;
          is_free_line?: boolean | null;
          sort_order?: number;
        }>;
      });
    },
  });
};

export const useUpsertCatalogPackageTemplate = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: customerId } = useCurrentClinicCustomerId();

  return useMutation({
    mutationFn: async (input: UpsertCatalogPackageTemplateInput) => {
      if (!customerId) throw new Error(i18n.t("erp:phase8.catalogPackages.errors.noClinicSelected"));

      const { data: auth } = await supabase.auth.getUser();
      const userId = auth.user?.id ?? null;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: upserted, error: upsertError } = await (supabase as any)
        .from("catalog_packages")
        .upsert({
          customer_id: customerId,
          item_id: input.itemId,
          charge_mode: input.chargeMode,
          controls_sessions: input.controlsSessions ?? true,
          default_sessions: Math.max(1, Math.floor(input.defaultSessions)),
          notes: input.notes?.trim() || null,
          created_by: userId,
        }, { onConflict: "customer_id,item_id" })
        .select("id")
        .single();

      if (upsertError) throw upsertError;

      const templateId = upserted.id as string;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: clearError } = await (supabase as any)
        .from("catalog_package_items")
        .delete()
        .eq("catalog_package_id", templateId);

      if (clearError) throw clearError;

      const cleanItems = input.items
        .filter((item) => item.itemId || item.itemNameSnapshot.trim())
        .map((item, index) => ({
          catalog_package_id: templateId,
          item_id: item.itemId ?? null,
          item_name_snapshot: item.itemNameSnapshot.trim(),
          unit_price_snapshot: Math.max(0, item.unitPriceSnapshot),
          sessions_total: Math.max(1, Math.floor(item.sessionsTotal)),
          is_free_line: item.isFreeLine,
          sort_order: index,
        }));

      if (cleanItems.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error: insertItemsError } = await (supabase as any)
          .from("catalog_package_items")
          .insert(cleanItems);
        if (insertItemsError) throw insertItemsError;
      }

      return templateId;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["catalog-package-templates"] });
      queryClient.invalidateQueries({ queryKey: ["catalog-package-template"] });
      toast({ title: i18n.t("erp:phase8.catalogPackages.savedTitle") });
    },
    onError: (error: Error) => {
      toast({
        title: i18n.t("erp:phase8.catalogPackages.saveErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
};
